import { ImageInfo } from "./ImageInfo";
import { PositionsData } from "./PositionsEditor/PositionsData";
import { ProjectInfo } from "./ProjectInfo";
import { clamp } from "./MathHelpers";
import CgoCardSide from "../CgoCard";

const mandatoryPropertyNames = ["width", "height", "coords"];
export function isPropertyMandatory(propertyName: string) {
  return mandatoryPropertyNames.includes(propertyName);
}

export function getTreeDataAtPath(treeData: any, path: string[]): any {
  let current: any = treeData;
  for (let i = 0; i < path.length; i++) {
    current = current[path[i]];
  }
  return current;
}

export function extractImagesAtPath(
  modalContextKey: string,
  treeData: object,
  projectName: string,
  projectInfo: ProjectInfo | undefined,
) {
  const treeDataAtPath = getTreeDataAtPath(treeData, [modalContextKey]);
  return gatherPossibleImages(treeDataAtPath, [modalContextKey], projectName, projectInfo);
}

export const cardPrefix = "__card-";

function gatherCardImages(
  data: any,
  componentName: string,
  projectInfo: ProjectInfo | undefined,
  possibleImages: ImageInfo[],
) {
  gatherImagesInConditions(data.cardBack, possibleImages);
  gatherImagesInConditions(data.__cond_cardBack, possibleImages);

  const deckName = componentName.substring(cardPrefix.length);

  if (projectInfo === undefined || projectInfo.decks === undefined || projectInfo.decks[deckName] === undefined) {
    return;
  }

  const deckData = projectInfo.decks[deckName];
  for (let cardId in deckData.cards) {
    possibleImages.push({
      type: "reactImg",
      cardId: cardId,
      deckInfo: deckData,
      name: `${deckName} - ${cardId}`,
      reactElement: <CgoCardSide cardId={cardId} deckInfo={deckData} globalAssets={projectInfo.globalAssets} />,
    });
  }
}

function gatherImagesInConditions(target: any, possibleImages: ImageInfo[]) {
  if (target === undefined) {
    return;
  }
  if (!Array.isArray(target)) {
    if (typeof target === "string") {
      possibleImages.push({ type: "src", value: target });
    }
  } else {
    target.forEach((item: { result: string }) => {
      if (
        item.result &&
        possibleImages.find((image) => image.type === "src" && image.value === item.result) === undefined
      ) {
        possibleImages.push({ type: "src", value: item.result });
      }
    });
  }
}

export function gatherPossibleImages(
  data: any,
  path: string[],
  projectName: string,
  projectInfo: ProjectInfo | undefined,
): ImageInfo[] {
  const objectName = path[path.length - 1];
  let possibleImages: ImageInfo[] = [];

  gatherImagesInConditions(data.image, possibleImages);
  gatherImagesInConditions(data.__cond_image, possibleImages);
  if (objectName.startsWith(cardPrefix)) {
    gatherCardImages(data, objectName, projectInfo, possibleImages);
  }

  return possibleImages;
}

export function getPositionObjectNames(positionObject: PositionsData) {
  if (positionObject === undefined) {
    return [];
  }
  let positionObjects: string[] = [];
  Object.entries(positionObject).map(([key, value]) => {
    if (typeof value === "object" && Array.isArray(value) === false) {
      positionObjects.push(key);
    }
  });
  return positionObjects;
}

export function getScrollbarWidth() {
  // Creating an invisible container
  const outer = document.createElement("div");
  if (outer === null) return 0;
  outer.style.visibility = "hidden";
  outer.style.overflow = "scroll"; // forcing scrollbar to appear
  document.body.appendChild(outer);

  // Creating an inner div and adding it to the outer div
  const inner = document.createElement("div");
  outer.appendChild(inner);

  // Calculating the scrollbar width
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

  // Removing the divs
  if (outer.parentNode === null) return 0;
  outer.parentNode.removeChild(outer);

  return scrollbarWidth;
}

export interface IndexableObject {
  [index: string]: any;
}

export function renameObjectKey(oldObj: IndexableObject, oldKey: string, newKey: string) {
  const keys = Object.keys(oldObj);
  return keys.reduce((acc: any, val) => {
    if (val === oldKey) {
      acc[newKey] = oldObj[oldKey];
    } else {
      acc[val] = oldObj[val];
    }
    return acc;
  }, {});
}

export function reducePathUntilKeyword(path: string[], keyword: string) {
  const keywordIndex = path.findIndex((segment) => segment.endsWith(keyword));
  if (keywordIndex === -1) return path; // Return the original if keyword not found
  return path.slice(keywordIndex + 1);
}

export function parseIntWithinLimits(value: string, min?: number, max?: number): number {
  const minLimit = min === undefined ? 0 : min;
  const maxLimit = max === undefined ? 10000 : max;
  return clamp(isNaN(parseInt(value)) ? 0 : parseInt(value), minLimit, maxLimit);
}
